// client/src/utils/costUtils.js

const n = (v, f = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : f;
};

const gramsFromYield = (p) => {
  const qty = n(p?.componentYield);
  const unit = (p?.componentYieldUnit || "g").toLowerCase();
  if (!qty) return 0;
  if (unit === "g") return qty;
  if (unit === "kg") return qty * 1000;
  // if ml/l were ever used for a component, we’d need density; we assume g
  if (unit === "ml") return qty; // fallback 1 g/ml
  if (unit === "l") return qty * 1000;
  return 0;
};

export const sumIngredientCost = (profile) =>
  (profile?.ingredients || []).reduce(
    (sum, i) => sum + n(i.quantity) * n(i.costPerUnitSnapshot),
    0
  );

export const sumPackagingCost = (profile) =>
  profile?.includePackaging
    ? (profile.packaging || []).reduce((s, p) => s + n(p.unitCost), 0)
    : 0;

export const laborCost = (profile) =>
  profile?.includeLabor
    ? (n(profile.laborMinutes) / 60) * n(profile.laborRate)
    : 0;

export const adjustmentsCost = (profile) =>
  (profile?.manualAdjustments || []).reduce((s, a) => s + n(a.amount), 0);

/** $ per gram for a populated reusable component profile */
export const componentCostPerGram = (componentProfile) => {
  if (!componentProfile) return 0;
  const batchCost =
    sumIngredientCost(componentProfile) +
    sumPackagingCost(componentProfile) +
    laborCost(componentProfile) +
    adjustmentsCost(componentProfile);

  const yG = gramsFromYield(componentProfile);
  return yG > 0 ? batchCost / yG : 0;
};

/** Total cost of all nested components in a product profile */
export const sumComponentsCost = (profile) =>
  (profile?.components || []).reduce((sum, c) => {
    // require the component doc to be populated for cost-per-gram
    const compProfile =
      typeof c.componentId === "object" && c.componentId ? c.componentId : null;
    if (!compProfile) return sum;
    const cpg = componentCostPerGram(compProfile); // $/g
    return sum + n(c.usedGrams) * cpg;
  }, 0);

/** Main: per-unit cost for a product (not for components table) */
export const calculateUnitCost = (profile) => {
  if (!profile) return 0;

  const ing = sumIngredientCost(profile);
  const comps = sumComponentsCost(profile);
  const pkg = sumPackagingCost(profile);
  const lab = laborCost(profile);
  const adj = adjustmentsCost(profile);

  const subtotal = ing + comps + pkg + lab + adj;

  // If you’re using batchSize > 1 for multi-unit batches, divide here
  const perUnitBeforeWaste = subtotal / Math.max(1, n(profile.batchSize, 1));

  const wastePct = n(profile.wasteBufferPercent);
  return perUnitBeforeWaste * (1 + wastePct / 100);
};
